import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { GridSortModel } from '../lib/dataGrid';
import { useProjectDocs } from '../db/useScopedDocs';
import { useSiblingNavState, type DialogBackState } from './dialogBackTrail';
import { compareNatural } from '../utils/compare';

/** One neighbour of the record a dialog is editing. */
export interface SiblingLink {
    /** Its own identity, as the app bar shows it: "02.08.2026", "12", "P1.jpg". */
    label: string;
    /** Its editor route — a real href, so it can be middle-clicked. */
    to: string;
    /** Open it through the unsaved-changes guard — a plain click's handler. */
    onOpen: () => void;
}

export interface RecordSiblings {
    previous?: SiblingLink;
    next?: SiblingLink;
}

/**
 * The record before and after the open one, in the order its GRID DEFAULT sorts
 * them (see recordSortModel). The grid's live sorting and filters are
 * deliberately NOT consulted: a dialog is reached from its grid, a deep link,
 * the cross-project Auswertung and the field app, and only one of those has a
 * grid state to read — one order that always holds beats a "next" that depends
 * on where the dialog was opened from.
 */
export function useRecordSiblings<
    Row extends { id: string; projectId: string }
>({
    collection,
    projectId,
    currentId,
    sortModel,
    label,
    requestNavigate
}: {
    /** The RxDB collection the grid lists — 'diaries', 'finds', … */
    collection: string;
    projectId: string;
    /** The open record, or '' in a `new` dialog (which has no neighbours). */
    currentId: string;
    /** The grid's default sort — `recordSortModel(<GRID>)` for the record grids. */
    sortModel: GridSortModel;
    /** How a neighbour names itself in the app bar. */
    label: (row: Row) => string;
    /** `guardedNav.requestNavigate` — leaving a dirty form has to ask first. */
    requestNavigate: (to: string, state?: DialogBackState) => void;
}): RecordSiblings {
    const { pathname } = useLocation();
    // Project-scoped like every record grid — a family shares its Befunde, but
    // each project's screen lists its own.
    const { results: rows } = useProjectDocs<Row>(collection, projectId);

    // One column decides, as in the grids themselves; ties fall back to the id
    // so the order cannot flip between two records that read the same.
    const field = sortModel[0]?.field ?? '';
    const descending = sortModel[0]?.sort === 'desc';
    const ordered = useMemo(() => {
        const valueOf = (row: Row) =>
            String((row as Record<string, unknown>)[field] ?? '');
        return [...(rows as Row[])].sort(
            (left, right) =>
                (descending ? -1 : 1) *
                    compareNatural(valueOf(left), valueOf(right)) ||
                compareNatural(left.id, right.id)
        );
    }, [rows, field, descending]);

    return useSiblingLinks({
        ordered,
        currentId,
        label,
        // Derived from the CURRENT url rather than a route table, so office
        // and field each stay in their own half of the app for free.
        to: (row) => pathname.replace(/[^/]+\/edit\/?$/, `${row.id}/edit`),
        requestNavigate
    });
}

/**
 * The neighbours of `currentId` in an ALREADY ORDERED list — the half every
 * caller shares. Split out for the Planum/Profil pager, whose neighbours are
 * neither a whole collection nor ordered by a grid column.
 */
export function useSiblingLinks<Row extends { id: string }>({
    ordered,
    currentId,
    label,
    to,
    requestNavigate
}: {
    /** The records to page through, in the order the pager steps them. */
    ordered: readonly Row[];
    currentId: string;
    label: (row: Row) => string;
    to: (row: Row) => string;
    requestNavigate: (to: string, state?: DialogBackState) => void;
}): RecordSiblings {
    // Paging carries its OWN router state, not the forward-trail a reference
    // chip attaches: a step count, so closing lands on the list rather than
    // walking back through every record paged past.
    const navState = useSiblingNavState();

    const index = currentId
        ? ordered.findIndex((row) => row.id === currentId)
        : -1;
    if (index < 0) {
        return {};
    }

    const link = (row: Row | undefined): SiblingLink | undefined => {
        if (!row) {
            return undefined;
        }
        const target = to(row);
        return {
            label: label(row),
            to: target,
            onOpen: () => requestNavigate(target, navState)
        };
    };

    return {
        previous: link(ordered[index - 1]),
        next: link(ordered[index + 1])
    };
}
